import { useEffect, useRef, useState } from "react";
import { useI18n } from "../i18n";

/**
 * Full-cover loading gate for the Grid surface. While the parcels and the
 * canonical zone overlay (boulevards / reserve marks / dots) are still
 * resolving, this paints a clean obsidian screen with a progress bar so the
 * grid never appears to "pop in" piece by piece (which reads as buggy). When
 * `ready` flips true the bar completes and the whole overlay fades out at once,
 * revealing the fully-painted grid. Purely presentational — pointer-events off.
 */
export function GridLoadingOverlay({ ready }: { ready: boolean }) {
  const { t } = useI18n();
  const [progress, setProgress] = useState(10);
  const [gone, setGone] = useState(false);
  const doneRef = useRef(false);

  // Creep toward ~90% while loading (never reaches 100 until ready).
  useEffect(() => {
    if (ready) return;
    const iv = window.setInterval(() => {
      setProgress((p) => (p < 90 ? p + Math.max(0.4, (92 - p) * 0.035) : p));
    }, 120);
    return () => window.clearInterval(iv);
  }, [ready]);

  // On ready: fill to 100%, fade out, then unmount.
  useEffect(() => {
    if (!ready || doneRef.current) return;
    doneRef.current = true;
    setProgress(100);
    const t = window.setTimeout(() => setGone(true), 560);
    return () => window.clearTimeout(t);
  }, [ready]);

  if (gone) return null;

  return (
    <div
      data-testid="grid-loading-overlay"
      data-ready={ready ? "true" : "false"}
      className="absolute inset-0 z-[70] flex flex-col items-center justify-center pointer-events-none transition-opacity duration-500 ease-out"
      style={{ background: "#05080d", opacity: ready ? 0 : 1 }}
    >
      <div className="flex flex-col items-center gap-5">
        <div
          className="grid-load-diamond"
          style={{
            width: 26,
            height: 26,
            transform: "rotate(45deg)",
            border: "1.5px solid rgba(45, 182, 240,0.9)",
            borderRadius: 4,
            boxShadow: "0 0 14px rgba(45, 182, 240,0.45)",
          }}
        />
        <div className="text-[11px] font-mono uppercase tracking-[0.34em] text-[rgba(180,220,230,0.75)]">
          {t("grid.loading.initializing")}
        </div>
        <div className="w-[240px] max-w-[62vw]">
          <div className="h-[3px] w-full rounded-full bg-[rgba(255,255,255,0.08)] overflow-hidden">
            <div
              className="h-full rounded-full transition-[width] duration-200 ease-out"
              style={{
                width: `${Math.min(100, progress)}%`,
                background: "linear-gradient(90deg, rgba(45, 182, 240,0.35), rgba(45, 182, 240,0.95))",
                boxShadow: "0 0 10px rgba(45, 182, 240,0.6)",
              }}
            />
          </div>
          <div className="mt-2 flex justify-between text-[9px] font-mono tracking-[0.2em] text-[rgba(150,175,190,0.55)]">
            <span>THE GRID</span>
            <span data-testid="grid-loading-pct">{Math.round(Math.min(100, progress))}%</span>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes gridLoadPulse { 0%,100% { opacity: .55; } 50% { opacity: 1; } }
        .grid-load-diamond { animation: gridLoadPulse 1.3s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .grid-load-diamond { animation: none; } }
      `}</style>
    </div>
  );
}
